// classifySendError.js — S-B6 failed-turn classification (adversarial P0-3).
//
// Today a non-ok POST / throw lands in `catch(_err){}` and is swallowed: orphaned user bubble,
// spinner hung forever, no recovery. This pure classifier maps an HTTP status (or a network/
// undefined "no response") to one of four recovery dispositions the UI renders:
//
//   'awaiting'  — 409 whose server error says the Session is awaiting the human's input (an
//                 unresolved approval/ask_user card). "Still replying" would be a lie here — the
//                 agent is waiting on YOU, so the copy points at the card instead of a blind retry.
//   'busy'      — any other 409: the agent is still replying. "Vesta is still replying" (retry later).
//   'fatal'     — 422: a validation/contract failure that retrying the SAME body won't fix.
//   'retryable' — 5xx server errors AND network failures (status 0 / undefined / no response):
//                 a "Couldn't send. Retry" affordance re-POSTs the SAME messageId (idempotent —
//                 the backend dedups on event_id, so retry never duplicates the turn).
//
// serverError is the backend's JSON `error` string when the caller captured one. Optional —
// omitting it preserves the original three-bucket behavior byte-for-byte (409 → 'busy').
// Pure + import-free → node-testable under `node --test`.

export function classifySendError(status, serverError) {
  if (status === 409) {
    return /awaiting your input/i.test(String(serverError || "")) ? "awaiting" : "busy"
  }
  if (status === 422) return "fatal"
  // Signed out (401) or no access (403): re-sending the same request can't succeed either.
  if (status === 401 || status === 403) return "fatal"
  // 5xx server errors are retryable; so is "no response" (network drop, status 0/undefined/null).
  return "retryable"
}

// Plain-language reason for a failed send, shown in the failed turn's notice instead of a bare
// "Message couldn't be sent." Maps the backend's known refusal strings (Sessions::AttachmentParams,
// the model/effort allowlists, auth) to what happened AND what to do about it. Anything unknown
// falls back to a generic line per kind — never the raw server string (it can be technical).
export function sendFailureMessage(kind, status, serverError) {
  const err = String(serverError || "")
  if (status === 401) return "You've been signed out. Refresh the page, sign back in, then send again."
  if (status === 403) return "Your account doesn't have access to the Vesta Agent chat. Ask your Vesta contact to turn it on."
  if (/unsupported attachment type/i.test(err)) {
    return "That file type can't be attached. Use a PNG, JPEG or WebP image, or a PDF. Video isn't supported yet."
  }
  if (/exceeds the size limit/i.test(err)) {
    return "That file is too large. Images can be up to 10MB and PDFs up to 20MB."
  }
  if (/at most \d+ attachments/i.test(err)) return "You can attach up to 4 files per message. Remove one and send again."
  if (/attachment/i.test(err)) {
    return "One of the attachments didn't upload correctly. Remove it, attach it again, then send."
  }
  if (/unsupported (model|effort)/i.test(err)) {
    return "That model setting isn't available right now. Pick a different model and send again."
  }
  if (kind === "fatal") {
    return "Message couldn't be sent. Try again, and if it keeps happening, contact Vesta support."
  }
  if (status >= 500) return "Vesta hit a problem sending that. Wait a moment, then retry."
  return "Couldn't send. Check your internet connection, then retry."
}
